export type KycProcessState = 'NOT_INITIATED' | 'OPEN' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED';

export type KycIdentificationProvider =
  | 'bankid'
  | 'ftn-auth'
  | 'onfido'
  | 'smartid'
  | 'smartid-lv'
  | 'smartid-retail-lv'
  | 'smartid-lt'
  | 'esteid'
  | 'esteid-mobiil'
  | 'nbid'
  | 'nbid-mobil'
  | 'mitid'
  | 'sbid'
  | 'mobile'
  | 'audkenni_app'
  | 'smartcard';

export type KycIdentificationOption = {
  key: string;
  url: string;
  provider: KycIdentificationProvider;
};

export type KycIdentificationConfig = {
  options: KycIdentificationOption[];
};

export type KycErrorType =
  | 'document-unusable'
  | 'no-suitable-account'
  | 'no-face-detected'
  | 'multiple-faces-detected'
  | 'unclear-images'
  | 'unclear-document-image'
  | 'unclear-selfie-image'
  | 'unknown';

export type KycError = {
  key: KycErrorType;
  error: string;
};

export type KycProcess = {
  id: number;
  userId: string;
  createdAt: string;
  completedAt: string | null;
  deadline: string;
  state: KycProcessState;
  attemptCount: number;
  config: KycIdentificationConfig;
  error: KycError | null;
};

type KybProcessState = 'OPEN' | 'PENDING_REVIEW' | 'REJECTED' | 'COMPLETED';

type MerchantCreationSource = 'mos' | 'unknown';

export type KybProcess = {
  id: number;
  merchantId: string;
  createdAt: string;
  countryCode: string;
  completedAt: string;
  deadline: string;
  state: KybProcessState;
  merchantCreationSource: MerchantCreationSource | null;
};

export type FieldKey =
  | 'ASSOCIATED_ENTITY'
  | 'BANK_ACCOUNT_NUMBER'
  | 'BANK_ACCOUNT_OWNER'
  | 'BANK_ACCOUNT_TYPE'
  | 'BANK_IDENTIFIER'
  | 'BANK_PAYOUT_RECIPIENTS'
  | 'BANK_STATEMENT'
  | 'CITY'
  | 'COMPANY_NAME'
  | 'COMPANY_REGISTRATION_DATE'
  | 'COMPANY_REGISTRATION_NUMBER'
  | 'COUNTRY'
  | 'DATE_OF_BIRTH'
  | 'DATE_OF_EXPIRATION'
  | 'EMAIL'
  | 'FIELD_OF_INDUSTRY'
  | 'FULL_NAME'
  | 'IS_MAIN_REPRESENTATIVE'
  | 'LEGAL_FORM'
  | 'LUCID_ID'
  | 'NATIONAL_ID'
  | 'NATIONALITY'
  | 'OFFICIAL_POSITION'
  | 'PHONE_NUMBER'
  | 'PLACE_OF_BIRTH'
  | 'POSTAL_CODE'
  | 'REGISTRY_EXTRACT'
  | 'STREET_ADDRESS'
  | 'TAX_COUNTRY'
  | 'TIN'
  | 'USER_ID'
  | 'IS_VAT_LIABLE'
  | 'VAT_ID';

type KybFieldOptions = {
  optional: boolean;
};

export type KybDataType = 'text' | 'date' | 'document' | 'list' | 'boolean';

export type KybField = {
  key: FieldKey;
  dataType: KybDataType;
  options: KybFieldOptions;
};

export type FieldSetKey =
  | 'businessDetails'
  | 'businessRepresentative'
  | 'uboDetails'
  | 'pepDetails'
  | 'bankAccountDetails'
  | 'documentDetails';

type KybFieldSet = {
  key: FieldSetKey;
  fields: KybField[];
};

export type KybSection = {
  key: 'basicCompanyInfo' | 'beneficiaries' | 'bankAccounts' | 'documents';
  fieldSets: KybFieldSet[];
};

export type KybProcessConfiguration = {
  sections: KybSection[];
};

export type KybSubmissionResponse = {
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
  data: KybSubmissionData | null;
  documents: KybSubmissionDocument[];
  review: KybProcessResubmissionReview | null;
};

export type KybProcessResubmissionReview = {
  feedback: string;
};

export type KybSubmissionDocument = {
  id: number;
  submittedBy: string;
  submittedAt: string;
  documentType: DocumentType;
  originalFilename: string | null;
  contentType: string | null;
  documentUrl: string | null;
};

export type KybSubmissionData = {
  basicCompanyInfo: BasicCompanyInfo | null;
  beneficiaries: Beneficiaries | null;
  bankAccounts: BankAccounts | null;
};

export type BasicCompanyInfo = {
  businessDetails: BusinessDetails | null;
  businessRepresentatives: BusinessRepresentative[] | null;
};

export type FieldValue<T> = {
  value: T | null;
};

export type TinDetails = {
  countryCode?: string;
  number?: string;
};

const legalFormValues = [
  'PUBLIC_LIMITED_COMPANY',
  'PRIVATE_LIMITED_COMPANY',
  'PARTNERSHIP',
  'SOLE_TRADER',
  'PUBLIC_AUTHORITY',
  'NON_PROFIT_ORGANISATION',
  'BRANCH',
  'FOREIGN_COMPANY',
  'OTHER',
] as const;

export type LegalForm = (typeof legalFormValues)[number];

export const isLegalFormValue = (value: string | null): value is LegalForm => {
  return !!legalFormValues.find(legalForm => legalForm === value);
};

export type BusinessDetails = {
  entityUUID: string | null;
  companyName: FieldValue<string> | null;
  streetAddress: FieldValue<string> | null;
  city: FieldValue<string> | null;
  postalCode: FieldValue<string> | null;
  countryCode: FieldValue<string> | null;
  registrationNumber: FieldValue<string> | null;
  isVatLiable: FieldValue<boolean> | null;
  vatId: FieldValue<string> | null;
  tin: FieldValue<string> | null;
  additionalTins: FieldValue<TinDetails[]> | null;
  lucidId: FieldValue<string> | null;
  legalForm: FieldValue<LegalForm> | null;
  dateOfRegistration: FieldValue<string> | null;
  taxCountryCode: FieldValue<string> | null;
  fieldOfIndustry: FieldValue<string> | null;
  phoneNumber: FieldValue<string> | null;
};

export type BusinessRepresentative = {
  entityUUID: string | null;
  fullName: FieldValue<string> | null;
  dateOfBirth: FieldValue<string> | null;
  placeOfBirth: FieldValue<string> | null;
  nationality: FieldValue<string> | null;
  nationalId: FieldValue<string> | null;
  streetAddress: FieldValue<string> | null;
  city: FieldValue<string> | null;
  postalCode: FieldValue<string> | null;
  countryOfResidence: FieldValue<string> | null;
  isMainRepresentative: FieldValue<boolean> | null;
  userId: FieldValue<string> | null;
};

export type Beneficiaries = {
  isRepresentativeSoleBusinessOwner: boolean | null;
  uboDetails: UboDetail[] | null;
  pepDetails: PepDetail[] | null;
};

export type BankAccounts = {
  bankAccountDetails: BankAccountDetails[] | null;
};

export type BankAccountDetails = {
  entityUUID: string | null;
  accountType: FieldValue<BankAccountType> | null;
  accountOwner: FieldValue<string> | null;
  accountNumber: FieldValue<string> | null;
  bankId: FieldValue<string> | null;
  payoutRecipients: FieldValue<string[]> | null;
};

export type UboDetail = {
  entityUUID: string | null;
  fullName: FieldValue<string> | null;
  streetAddress: FieldValue<string> | null;
  city: FieldValue<string> | null;
  postalCode: FieldValue<string> | null;
  nationality: FieldValue<string> | null;
  dateOfBirth: FieldValue<string> | null;
  nationalId: FieldValue<string> | null;
  country: FieldValue<string> | null;
};

export type PepDetail = {
  entityUUID: string | null;
  fullName: FieldValue<string> | null;
  officialPosition: FieldValue<string> | null;
  countryCode: FieldValue<string> | null;
  expirationDate: FieldValue<string> | null;
};

export type DocumentType = 'REGISTRATION_CERTIFICATE' | 'BANK_STATEMENT';
export type BankAccountType = 'IBAN' | 'BBAN';
